/**
 * Utilidades compartidas para parsing y normalización de datos
 * Elimina código duplicado en múltiples servicios y clases de datos
 * 
 * SRP: Responsabilidad única de parse/format de valores
 */

export class ParsingUtilities {
  /**
   * Parsea un valor a número entero, manejando diferentes formatos
   * Soporta: strings, numbers, valores con separadores (1.000,50 → 1000)
   */
  static parseIntegerValue(rawValue: any): number | null {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    if (typeof rawValue === 'number') {
      return Number.isNaN(rawValue) ? null : Math.trunc(rawValue);
    }

    const normalizedValue = `${rawValue}`.replaceAll('.', '').replace(',', '.');
    const parsedValue = Number(normalizedValue);
    return Number.isNaN(parsedValue) ? null : Math.trunc(parsedValue);
  }

  /**
   * Parsea una fecha en formato YYYY-MM-DD usando zona horaria LOCAL
   * (no UTC, para evitar offset de -1 día en timezones negativas)
   * 
   * Soporta:
   * - String "YYYY-MM-DD" → Date at midnight local time
   * - Date object → retorno sin cambios
   * - null/undefined → undefined
   * 
   * CRÍTICO: No usar new Date(string + 'T00:00:00') que interpreta como UTC
   */
  static parseLocalDate(dateValue: any): Date | undefined {
    if (dateValue === null || dateValue === undefined || dateValue === '') {
      return undefined;
    }

    // Si ya es Date object, retornarlo tal cual
    if (dateValue instanceof Date) {
      return Number.isNaN(dateValue.getTime()) ? undefined : dateValue;
    }

    // Si es string YYYY-MM-DD, parsear con zona horaria local
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);  // Constructor usa local timezone
    }

    // Si es cualquier otro formato string, intentar parse
    const parsedDate = new Date(dateValue);
    return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
  }

  /**
   * Parsea una fecha opcional de múltiples formatos
   * Soporta: ISO strings (YYYY-MM-DD), Date objects, etc.
   */
  static parseOptionalDate(dateValue: any): Date | undefined {
    if (dateValue === null || dateValue === undefined || dateValue === '') {
      return undefined;
    }

    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);  // Constructor usa local timezone
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return undefined;
    }

    return parsedDate;
  }

  /**
   * Formatea una Date a string ISO para API backend
   * Formato: YYYY-MM-DD
   * Soporta Date objects y strings (si ya es string, lo retorna tal cual)
   */
  static formatDateForApi(dateValue: Date | string | undefined): string | undefined {
    if (!dateValue) {
      return undefined;
    }

    // Si ya es string en formato YYYY-MM-DD, retornarlo tal cual
    if (typeof dateValue === 'string') {
      return dateValue;
    }

    // Si es Date object
    if (typeof dateValue === 'object' && dateValue instanceof Date) {
      if (Number.isNaN(dateValue.getTime())) {
        return undefined;
      }

      const year = dateValue.getFullYear();
      const month = `${dateValue.getMonth() + 1}`.padStart(2, '0');
      const day = `${dateValue.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return undefined;
  }

  /**
   * Normaliza un texto opcional, removiendo espacios
   * Retorna null si después de trim queda vacío
   */
  static normalizeOptionalText(rawValue: any): string | null {
    if (rawValue === null || rawValue === undefined) {
      return null;
    }

    const normalized = `${rawValue}`.trim();
    return normalized.length ? normalized : null;
  }

  /**
   * Extrae mensaje de error desde respuesta HTTP o error object
   * Busca en: error.error.detail → error.error.message → error.error.title → ...
   */
  static getErrorDetail(error: any): string {
    return error?.error?.detail ||
           error?.error?.message ||
           error?.error?.title ||
           error?.message ||
           '';
  }
}
